import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Users, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getPlayers, getTeamStaff } from "@/lib/api";

const teamCategories = [
  { label: "Eerste ploeg", value: "Eerste ploeg", slugs: ["eerste-ploeg", "eerste"] },
  { label: "Jong FTG", value: "Jong FTG", slugs: ["jong-ftg", "jong"] },
  { label: "C", value: "C", slugs: ["c"] },
  { label: "U17", value: "U17", slugs: ["u17"] },
  { label: "U15", value: "U15", slugs: ["u15"] },
  { label: "U13", value: "U13", slugs: ["u13"] },
];

// Normalize URL parameter to category code
const normalizeCategory = (param) => {
  if (!param) return "Eerste ploeg";
  // Check if it's already a valid code
  const directMatch = teamCategories.find(c => c.value === param);
  if (directMatch) return directMatch.value;
  // Check slugs
  const slugMatch = teamCategories.find(c => 
    c.slugs?.some(s => s.toLowerCase() === param.toLowerCase())
  );
  if (slugMatch) return slugMatch.value;
  return "Eerste ploeg";
};

const positionColors = {
  Keeper: "text-yellow-500 border-yellow-500/30 bg-yellow-500/10",
  Defender: "text-blue-500 border-blue-500/30 bg-blue-500/10",
  Winger: "text-green-500 border-green-500/30 bg-green-500/10",
  Pivot: "text-red-500 border-red-500/30 bg-red-500/10",
};

const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%2364748b' width='200' height='200'/%3E%3Ccircle cx='100' cy='70' r='35' fill='%23cbd5e1'/%3E%3Cpath d='M40 160c0-33 26.9-60 60-60s60 27 60 60' fill='%23cbd5e1'/%3E%3C/svg%3E";

export default function Teams() {
  const { category } = useParams();
  const [players, setPlayers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(normalizeCategory(category));
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  useEffect(() => {
    if (category) {
      setActiveCategory(normalizeCategory(category));
    }
  }, [category]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [playersRes, staffRes] = await Promise.all([
          getPlayers(),
          getTeamStaff({ team_category: activeCategory })
        ]);
        
        const allPlayers = Array.isArray(playersRes.data) ? playersRes.data : playersRes.data?.data || [];
        const filteredPlayers = allPlayers.filter(p => p.team_category === activeCategory);
        
        const allStaff = Array.isArray(staffRes.data) ? staffRes.data : staffRes.data?.data || [];
        const filteredStaff = allStaff.filter(s => s.team_category === activeCategory);
        
        setPlayers(filteredPlayers);
        setStaff(filteredStaff);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeCategory]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const PlayerCard = ({ player }) => {
    const positionColors = {
      Keeper: { 
        gradient: isDark ? "from-amber-500/20 via-amber-600/10 to-transparent" : "from-amber-400/30 via-amber-300/20 to-transparent",
        badge: isDark ? "bg-amber-500/20 text-amber-400 border-amber-500/40" : "bg-amber-500 text-white border-amber-600",
        number: isDark ? "text-amber-400" : "text-amber-600"
      },
      Defender: { 
        gradient: isDark ? "from-blue-500/20 via-blue-600/10 to-transparent" : "from-blue-400/30 via-blue-300/20 to-transparent",
        badge: isDark ? "bg-blue-500/20 text-blue-400 border-blue-500/40" : "bg-blue-500 text-white border-blue-600",
        number: isDark ? "text-blue-400" : "text-blue-600"
      },
      Winger: { 
        gradient: isDark ? "from-emerald-500/20 via-emerald-600/10 to-transparent" : "from-emerald-400/30 via-emerald-300/20 to-transparent",
        badge: isDark ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-emerald-500 text-white border-emerald-600",
        number: isDark ? "text-emerald-400" : "text-emerald-600"
      },
      Pivot: { 
        gradient: isDark ? "from-rose-500/20 via-rose-600/10 to-transparent" : "from-rose-400/30 via-rose-300/20 to-transparent",
        badge: isDark ? "bg-rose-500/20 text-rose-400 border-rose-500/40" : "bg-rose-500 text-white border-rose-600",
        number: isDark ? "text-rose-400" : "text-rose-600"
      },
    };

    const colors = positionColors[player.position] || positionColors.Defender;

    return (
      <Card 
        className={`group relative overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
          isDark 
            ? 'bg-slate-900/80 border-white/10 hover:border-white/20 backdrop-blur-sm' 
            : 'bg-white border-slate-200 hover:border-slate-300 shadow-lg hover:shadow-2xl'
        }`}
        data-testid={`player-card-${player.id}`}
      >
        {/* Player Image Area */}
        <div className="relative h-56 overflow-hidden">
          {player.image_url ? (
            <>
              <img 
                src={getImageUrl(player.image_url)} 
                alt={player.name}
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                style={{ objectPosition: 'center 20%' }}
              />
              {/* Overlay gradient for text readability - darker for light mode */}
              <div className={`absolute inset-0 bg-gradient-to-t ${
                isDark 
                  ? 'from-slate-900 via-slate-900/40 to-transparent' 
                  : 'from-slate-900/80 via-slate-900/30 to-transparent'
              }`} />
            </>
          ) : (
            <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${colors.gradient} ${
              isDark ? 'from-slate-800 to-slate-900' : 'from-slate-200 to-slate-300'
            }`}>
              <div className="text-center">
                <span className={`text-8xl font-black font-['Chivo'] ${
                  isDark ? 'text-white/20' : 'text-slate-400/50'
                }`}>
                  {player.number}
                </span>
              </div>
            </div>
          )}
          
          {/* Number badge - top right */}
          <div className="absolute top-3 right-3">
            <div className={`px-3 py-1.5 rounded-lg backdrop-blur-md ${
              isDark 
                ? 'bg-black/50 border border-white/20' 
                : 'bg-white/90 border border-slate-200 shadow-md'
            }`}>
              <span className={`text-xl font-black font-['Chivo'] ${colors.number}`}>
                #{player.number}
              </span>
            </div>
          </div>
          
          {/* Position badge - top left */}
          <div className="absolute top-3 left-3">
            <span className={`inline-block px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border backdrop-blur-md ${colors.badge}`}>
              {player.position}
            </span>
          </div>
        </div>
        
        {/* Player Info */}
        <CardContent className="relative p-5">
          <div className="mb-4">
            <h3 className={`font-bold text-xl font-['Chivo'] tracking-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {player.name}
            </h3>
            {player.nationality && (
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {player.nationality}
              </p>
            )}
          </div>
          
          {/* Player Stats */}
          {player.stats && (player.stats.goals > 0 || player.stats.assists > 0 || player.stats.appearances > 0) ? (
            <div className={`grid grid-cols-3 gap-2 pt-4 border-t ${
              isDark ? 'border-white/10' : 'border-slate-200'
            }`}>
              <div className={`text-center p-2 rounded-lg ${
                isDark ? 'bg-white/5' : 'bg-slate-50'
              }`}>
                <span className={`block text-xl font-black font-['Chivo'] ${
                  isDark ? 'text-amber-400' : 'text-amber-600'
                }`}>
                  {player.stats.goals || 0}
                </span>
                <span className={`text-[10px] uppercase tracking-wider font-medium truncate block ${
                  isDark ? 'text-slate-500' : 'text-slate-500'
                }`}>
                  Goals
                </span>
              </div>
              <div className={`text-center p-2 rounded-lg ${
                isDark ? 'bg-white/5' : 'bg-slate-50'
              }`}>
                <span className={`block text-xl font-black font-['Chivo'] ${
                  isDark ? 'text-emerald-400' : 'text-emerald-600'
                }`}>
                  {player.stats.assists || 0}
                </span>
                <span className={`text-[10px] uppercase tracking-wider font-medium truncate block ${
                  isDark ? 'text-slate-500' : 'text-slate-500'
                }`}>
                  Assists
                </span>
              </div>
              <div className={`text-center p-2 rounded-lg ${
                isDark ? 'bg-white/5' : 'bg-slate-50'
              }`}>
                <span className={`block text-xl font-black font-['Chivo'] ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {player.stats.appearances || 0}
                </span>
                <span className={`text-[10px] uppercase tracking-wider font-medium truncate block ${
                  isDark ? 'text-slate-500' : 'text-slate-500'
                }`}>
                  Matches
                </span>
              </div>
            </div>
          ) : (
            <div className={`flex items-center justify-center py-3 rounded-lg ${
              isDark ? 'bg-white/5' : 'bg-slate-50'
            }`}>
              <span className={`text-xs uppercase tracking-wider ${
                isDark ? 'text-slate-500' : 'text-slate-400'
              }`}>
                Nog geen statistieken
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const StaffCard = ({ member }) => (
    <Card 
      className={`group overflow-hidden transition-all duration-300 hover:scale-[1.01] ${
        isDark 
          ? 'bg-slate-900/80 border-white/10 hover:border-amber-500/30 backdrop-blur-sm' 
          : 'bg-white border-slate-200 hover:border-amber-400/50 shadow-lg hover:shadow-xl'
      }`}
      data-testid={`staff-card-${member.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className={`absolute inset-0 rounded-full blur-md transition-opacity duration-300 opacity-0 group-hover:opacity-100 ${
              isDark ? 'bg-amber-500/20' : 'bg-amber-400/30'
            }`} />
            <img 
              src={member.image_url ? getImageUrl(member.image_url) : DEFAULT_AVATAR} 
              alt={member.name}
              className={`relative w-20 h-20 rounded-full object-cover ring-2 ring-offset-2 transition-all duration-300 ${
                isDark 
                  ? 'ring-white/20 ring-offset-slate-900 group-hover:ring-amber-500/50' 
                  : 'ring-slate-200 ring-offset-white group-hover:ring-amber-400'
              }`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold text-lg font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {member.name}
            </h3>
            <span className={`inline-block px-3 py-1 mt-2 rounded-full text-xs font-semibold uppercase tracking-wider ${
              isDark 
                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' 
                : 'bg-amber-100 text-amber-700 border border-amber-300'
            }`}>
              {member.role}
            </span>
            {member.bio && (
              <p className={`text-sm mt-3 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {member.bio}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen py-12" data-testid="teams-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Users className={`h-10 w-10 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
          <div>
            <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>TEAMS</h1>
            <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Ontmoet onze spelers en staff</p>
          </div>
        </div>

        {/* Team Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className={`mb-8 flex-wrap h-auto gap-1 p-1 ${isDark ? 'bg-slate-800/50 border border-white/10' : 'bg-slate-200 border border-slate-300'}`}>
            {teamCategories.map((team) => (
              <TabsTrigger 
                key={team.value}
                value={team.value}
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white px-6"
                data-testid={`team-tab-${team.value.toLowerCase()}`}
              >
                {team.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {teamCategories.map((team) => (
            <TabsContent key={team.value} value={team.value}>
              {loading ? (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={`h-72 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded-lg animate-pulse`} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-12">
                  {/* Staff Section */}
                  {staff.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3 mb-6">
                        <Shield className={`h-6 w-6 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                        <h2 className={`text-2xl font-bold font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Technische Staf
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {staff.map((member) => (
                          <StaffCard key={member.id} member={member} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Players Section */}
                  <div>
                    {staff.length > 0 && (
                      <div className="flex items-center gap-3 mb-6">
                        <Users className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        <h2 className={`text-2xl font-bold font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Spelers
                        </h2>
                      </div>
                    )}
                    {players.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {players.map((player) => (
                          <PlayerCard key={player.id} player={player} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Users className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                        <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Geen spelers gevonden voor {team.label}</p>
                        <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Spelers worden hier getoond zodra ze zijn toegevoegd</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
